import {
  Component, ChangeDetectionStrategy, input, output, signal, effect,
  ElementRef, viewChild, OnDestroy, inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as L from 'leaflet';
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { GeocodeService, GeocodeResult } from './geocode.service';

const DEFAULT_LAT = -33.4489;
const DEFAULT_LNG = -70.6693;
const DEFAULT_ZOOM = 12;

@Component({
  selector: 'app-location-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './location-picker.component.html',
  styleUrl: './location-picker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationPickerComponent implements OnDestroy {
  private geocode = inject(GeocodeService);
  private destroy$ = new Subject<void>();
  private search$ = new Subject<string>();
  private map?: L.Map;
  private marker?: L.Marker;
  private syncing = false;
  private resizeObserver?: ResizeObserver;

  address = input<string>('');
  latitude = input<number | null>(null);
  longitude = input<number | null>(null);
  readOnly = input<boolean>(false);

  locationChange = output<{ address: string; latitude: number | null; longitude: number | null }>();

  mapEl = viewChild<ElementRef<HTMLDivElement>>('mapEl');

  query = signal('');
  suggestions = signal<GeocodeResult[]>([]);
  loading = signal(false);
  geoError = signal<string | null>(null);
  currentLat = signal<number | null>(null);
  currentLng = signal<number | null>(null);
  currentAddress = signal('');

  constructor() {
    this.search$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((q) => {
        this.loading.set(true);
        return this.geocode.searchChile(q);
      }),
      takeUntil(this.destroy$)
    ).subscribe((results) => {
      this.suggestions.set(results);
      this.loading.set(false);
    });

    effect(() => {
      const el = this.mapEl()?.nativeElement;
      if (!el || this.map) return;
      queueMicrotask(() => this._initMap(el));
    });

    effect(() => {
      const addr = this.address() ?? '';
      const lat = this._toNum(this.latitude());
      const lng = this._toNum(this.longitude());
      if (this.syncing) return;
      this.currentAddress.set(addr);
      this.currentLat.set(lat);
      this.currentLng.set(lng);
      if (this.map && lat != null && lng != null) {
        this._setMarker(lat, lng, false);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.map?.remove();
    this.map = undefined;
  }

  onQueryInput(value: string) {
    this.query.set(value);
    this.geoError.set(null);
    if (!this.readOnly()) this.search$.next(value);
  }

  selectSuggestion(item: GeocodeResult) {
    this.suggestions.set([]);
    this.query.set('');
    this.currentAddress.set(item.displayName);
    this.currentLat.set(item.lat);
    this.currentLng.set(item.lng);
    this._setMarker(item.lat, item.lng, true);
    this._emit();
  }

  useMyLocation() {
    if (this.readOnly()) return;
    this.geoError.set(null);
    if (!navigator.geolocation) {
      this.geoError.set('Tu navegador no soporta geolocalización.');
      return;
    }
    this.loading.set(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        this.currentLat.set(lat);
        this.currentLng.set(lng);
        this._setMarker(lat, lng, true);
        this.geocode.reverse(lat, lng).pipe(takeUntil(this.destroy$)).subscribe((result) => {
          this.loading.set(false);
          if (result) {
            this.currentAddress.set(result.displayName);
            this.query.set('');
          }
          this._emit();
        });
      },
      () => {
        this.loading.set(false);
        this.geoError.set('No se pudo obtener la ubicación. Revisa los permisos del navegador.');
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }

  private _initMap(el: HTMLDivElement) {
    const lat = this.currentLat() ?? DEFAULT_LAT;
    const lng = this.currentLng() ?? DEFAULT_LNG;
    const hasCoords = this.currentLat() != null && this.currentLng() != null;

    this.map = L.map(el, {
      zoomControl: true,
      dragging: !this.readOnly(),
      scrollWheelZoom: !this.readOnly(),
      doubleClickZoom: !this.readOnly(),
      boxZoom: !this.readOnly(),
      keyboard: !this.readOnly()
    }).setView([lat, lng], hasCoords ? 15 : DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19
    }).addTo(this.map);

    if (hasCoords) {
      this._setMarker(lat, lng, false);
    }

    if (!this.readOnly()) {
      this.map.on('click', (e: L.LeafletMouseEvent) => {
        this._onMapPick(e.latlng.lat, e.latlng.lng);
      });
    }

    setTimeout(() => this.map?.invalidateSize(), 100);
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserver.observe(el);
  }

  private _onMapPick(lat: number, lng: number) {
    this.currentLat.set(lat);
    this.currentLng.set(lng);
    this._setMarker(lat, lng, false);
    this.loading.set(true);
    this.geocode.reverse(lat, lng).pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.loading.set(false);
      if (result) {
        this.currentAddress.set(result.displayName);
        this.query.set('');
      }
      this._emit();
    });
  }

  private _setMarker(lat: number, lng: number, fly: boolean) {
    if (!this.map) return;
    const icon = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = L.marker([lat, lng], {
        draggable: !this.readOnly(),
        icon
      }).addTo(this.map);

      if (!this.readOnly()) {
        this.marker.on('dragend', () => {
          const pos = this.marker!.getLatLng();
          this._onMapPick(pos.lat, pos.lng);
        });
      }
    }

    if (fly) {
      this.map.flyTo([lat, lng], 15, { duration: 0.6 });
    } else {
      this.map.setView([lat, lng], Math.max(this.map.getZoom(), 14));
    }
  }

  private _emit() {
    this.syncing = true;
    this.locationChange.emit({
      address: this.currentAddress(),
      latitude: this.currentLat(),
      longitude: this.currentLng()
    });
    queueMicrotask(() => { this.syncing = false; });
  }

  private _toNum(v: number | string | null | undefined): number | null {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
}
